// shellAst - one normalized command model over the bash and PowerShell trees.
//
// The two grammars describe the same idea with different shapes: bash has
// `command{name, argument...}` under an optional `redirected_statement`,
// PowerShell has `command{command_name, command_elements}` inside a
// `pipeline_chain`. A drafter should not have to know both, so this flattens
// each into the same record and keeps the differences here.
//
// THE FIELD THAT MATTERS IS `resolved`. A scanner reports what it matched and
// stays quiet about what it could not see, which is how "no writes found"
// comes to mean two incompatible things: nothing wrote, or the writer was
// spelled `$OUT`. Every argument here carries whether it is literal, every
// command carries whether all of its parts are, and the reading carries
// whether the parse covered the whole source. A caller that wants to claim a
// command was harmless must check them; that is the whole point of the field.
//
// VALUES, NOT NODES. Nothing here hands back a tree-sitter node. Nodes are
// owned by their tree, and parserHost evicts trees from a bounded cache, so a
// node retained past its eviction reads freed wasm memory. Drafters that need
// the tree itself go through `withSyntaxTree`, which holds it for the callback
// and no longer.
import { parseIsComplete, parseSource } from "./parserHost.js";

export const SHELL_LANGUAGES = Object.freeze(["bash", "powershell"]);

// How deep an interpreter payload is followed. Matches the signals catalog's
// `_MAX_WRAPPER_DEPTH`: `bash -c "pwsh -Command '...'"` is real, another turn
// of it is a puzzle, not a session.
export const MAX_NESTING_DEPTH = 3;
// One command line is a handful of statements. A generated one can be
// thousands, and every one of them costs a record.
const MAX_COMMANDS = 512;
const MAX_ARGS_PER_COMMAND = 128;

// Nodes whose presence means a span is not literal text. Listed per language
// because the two grammars name them differently, and a missing entry here
// would report a computed argument as a literal one - the error direction that
// makes a capability reading lie.
const UNRESOLVED_TYPES = {
  bash: new Set(["simple_expansion", "expansion", "command_substitution", "arithmetic_expansion", "process_substitution"]),
  powershell: new Set(["variable", "sub_expression", "expandable_string_literal", "script_block_expression"]),
};

// Bash spans that are literal despite containing a `$`-looking character.
const BASH_QUOTED = new Set(["raw_string", "string_content"]);

// A leading `/` is a flag in the cmd.exe convention (`/S`, `/Q`) and the first
// character of a path everywhere else. The convention only exists on the
// Windows side, so the slash form is read as a flag under the PowerShell
// grammar alone: under bash it swallowed every short absolute path - `/tmp`,
// `/etc`, `/var` - and silently dropped them out of the capability walk,
// because flags are not path candidates.
const isFlag = (text, language) => /^-{1,2}[^\s-]/.test(text)
  || (language === "powershell" && /^\/[A-Za-z][A-Za-z0-9]{0,7}$/.test(text));

/** Strip one layer of surrounding quotes from a span's text. */
function unquote(text) {
  const t = String(text ?? "");
  if (t.length >= 2 && ((t[0] === '"' && t.endsWith('"')) || (t[0] === "'" && t.endsWith("'")))) {
    return t.slice(1, -1);
  }
  return t;
}

// Backslash escapes inside a bash double-quoted span. Getting this wrong is not
// cosmetic: an interpreter payload is routinely a doubly-quoted program, and
// handing the next parse a source that still carries `\"` makes it read the
// escape as the start of a word. That showed up as a nested `bash -c` chain
// that terminated early and reported itself fully read.
const BASH_DQ_ESCAPE = /\\([\\"`$\n])/g;

/**
 * What a span would be worth after the shell finished with it, as far as that
 * can be known statically. Unresolved spans keep their source text - the
 * caller is told they are unresolved and must not treat this as a value.
 */
function spanValue(node, language) {
  if (language !== "bash") return unquote(node.text);
  switch (node.type) {
    case "raw_string":
      // Single quotes: no escape processing at all, which is the entire point
      // of the form.
      return node.text.slice(1, -1);
    case "string": {
      let out = "";
      for (let i = 0; i < node.namedChildCount; i += 1) {
        const child = node.namedChild(i);
        out += child.type === "string_content" ? child.text.replace(BASH_DQ_ESCAPE, "$1") : child.text;
      }
      return out;
    }
    case "concatenation": {
      let out = "";
      for (let i = 0; i < node.namedChildCount; i += 1) out += spanValue(node.namedChild(i), language);
      return out;
    }
    case "word":
      // An unquoted word carries its own escapes: `a\ b` is one word worth `a b`.
      return node.text.replace(/\\(.)/g, "$1");
    default:
      return unquote(node.text);
  }
}

/** The trailing path component, for matching a program by name not by install path. */
function basename(text) {
  const t = unquote(text).replace(/[\\/]+$/, "");
  const cut = Math.max(t.lastIndexOf("/"), t.lastIndexOf("\\"));
  const name = cut >= 0 ? t.slice(cut + 1) : t;
  return name.replace(/\.(?:exe|cmd|bat|ps1)$/i, "").toLowerCase();
}

/** Whether a node's subtree contains anything the shell would expand at run time. */
function spanIsResolved(node, language) {
  const unresolved = UNRESOLVED_TYPES[language] || new Set();
  const stack = [node];
  while (stack.length) {
    const n = stack.pop();
    if (unresolved.has(n.type)) {
      // A `$` inside single quotes is a literal dollar, not an expansion, and
      // the bash grammar still parses `string_content` under a quoted span.
      if (!(language === "bash" && BASH_QUOTED.has(n.parent?.type))) return false;
    }
    for (let i = 0; i < n.namedChildCount; i += 1) stack.push(n.namedChild(i));
  }
  return true;
}

const arg = (node, language) => ({
  text: node.text,
  value: spanValue(node, language),
  resolved: spanIsResolved(node, language),
  kind: isFlag(node.text, language) ? "flag" : "value",
});

// -- bash -----------------------------------------------------------------

function bashRedirects(commandNode) {
  const out = [];
  const parent = commandNode.parent;
  if (parent?.type !== "redirected_statement") return out;
  for (let i = 0; i < parent.namedChildCount; i += 1) {
    const node = parent.namedChild(i);
    if (node.type !== "file_redirect" && node.type !== "heredoc_redirect") continue;
    const target = node.childForFieldName("destination");
    if (!target) continue;
    // The operator is an anonymous child, so it is read off the source between
    // the body and the destination rather than from a field.
    const op = /(>>|>\||>&|>|<<<|<<|<)/.exec(node.text)?.[1] || ">";
    out.push({
      op,
      target: spanValue(target, "bash"),
      resolved: spanIsResolved(target, "bash"),
      writes: op.startsWith(">"),
    });
  }
  return out;
}

function bashHeredocBody(commandNode) {
  // `python3 - <<PY ... PY`: the payload is a sibling of the redirect, not an
  // argument, so an argument-only walk reports an interpreter that ran nothing.
  const parent = commandNode.parent;
  if (parent?.type !== "redirected_statement") return null;
  for (let i = 0; i < parent.namedChildCount; i += 1) {
    const node = parent.namedChild(i);
    if (node.type !== "heredoc_redirect") continue;
    for (let j = 0; j < node.namedChildCount; j += 1) {
      const child = node.namedChild(j);
      // `heredoc_end` is its own node, so the body text is already exactly the
      // payload - trimming a trailing word here would eat the last statement.
      if (child.type === "heredoc_body") return child.text;
    }
  }
  return null;
}

function readBashCommand(node) {
  const name = node.childForFieldName("name");
  if (!name) return null;
  const args = node.childrenForFieldName("argument")
    .slice(0, MAX_ARGS_PER_COMMAND)
    .map((a) => arg(a, "bash"));
  return {
    language: "bash",
    program: spanIsResolved(name, "bash") ? basename(name.text) : null,
    programRaw: name.text,
    args,
    redirects: bashRedirects(node),
    heredoc: bashHeredocBody(node),
    text: node.text,
    start: node.startIndex,
    end: node.endIndex,
  };
}

// -- powershell -----------------------------------------------------------

// `2>&1` carries a `merging_redirection_operator` and moves a stream; every
// `file_redirection_operator` form (`>`, `>>`, `2>`, `3>>`, `*>`) writes a
// file. An operator with no recoverable file name is reported as an unresolved
// write rather than skipped: the write is real even when its target is not
// readable, and skipping it is how `Get-Content x > $dest` cleared as inert.
function powershellRedirect(node) {
  let op = null;
  let target = null;
  for (let i = 0; i < node.namedChildCount; i += 1) {
    const child = node.namedChild(i);
    if (child.type === "file_redirection_operator") op = child.text;
    if (child.type === "redirected_file_name") {
      for (let j = 0; j < child.namedChildCount; j += 1) {
        const t = child.namedChild(j);
        if (t.type !== "command_argument_sep") { target = t; break; }
      }
    }
  }
  if (!op) return null;
  if (!target) return { op, target: null, resolved: false, writes: true };
  return {
    op,
    target: spanValue(target, "powershell"),
    resolved: spanIsResolved(target, "powershell"),
    writes: true,
  };
}

function readPowershellCommand(node) {
  const name = node.childForFieldName("command_name")
    // `& $exe -Force` names the program through an expression, which is exactly
    // the case a reading must call unresolved rather than skip.
    || node.childForFieldName("command_name_expr");
  if (!name) return null;
  const args = [];
  const redirects = [];
  const elements = node.childForFieldName("command_elements");
  if (elements) {
    for (let i = 0; i < elements.namedChildCount && args.length < MAX_ARGS_PER_COMMAND; i += 1) {
      const child = elements.namedChild(i);
      if (child.type === "command_argument_sep") continue;
      if (child.type === "redirection") {
        const redirect = powershellRedirect(child);
        if (redirect) redirects.push(redirect);
        continue;
      }
      // `Get-Content a.txt,b.txt` arrives as two tokens, the second spelled
      // `,b.txt`: the comma separates array items, it is not part of the value.
      const read = arg(child, "powershell");
      args.push(read.value.startsWith(",") ? { ...read, value: read.value.slice(1) } : read);
    }
  }
  const resolvedName = spanIsResolved(name, "powershell");
  return {
    language: "powershell",
    program: resolvedName ? basename(name.text) : null,
    programRaw: name.text,
    args,
    redirects,
    heredoc: null,
    text: node.text,
    start: node.startIndex,
    end: node.endIndex,
  };
}

// -- transparent prefixes ---------------------------------------------------

// Programs that change HOW something runs but not WHAT runs. Left unpeeled,
// `timeout 30 curl ...` reports its program as `timeout` and the curl - the
// part that reaches the network - becomes an argument nothing looks at. The
// signals catalog peels the same set for the same reason.
const TRANSPARENT = new Set([
  "sudo", "doas", "env", "nice", "ionice", "nohup", "timeout", "stdbuf", "setsid",
  "command", "exec", "time", "chronic", "unbuffer",
]);
// Elevation is peeled for matching and REMEMBERED, because it widens what the
// command underneath can reach even though it is not itself the command.
const ELEVATING = new Set(["sudo", "doas"]);
// Tokens a prefix consumes before the real program: its own flags, an
// environment assignment, and a bare duration or number (`timeout 30`).
const PREFIX_NOISE = /^(?:-|\/[A-Za-z]|[A-Za-z_]\w*=|\d+(?:\.\d+)?[smhd]?$)/;

/**
 * Move the command past any transparent prefixes it starts with.
 *
 * Returns the same record when there is nothing to peel, so callers do not
 * have to care. A prefix whose real program is computed leaves `program` null,
 * which is the honest answer and keeps the command unresolved.
 */
function peelTransparent(command) {
  let current = command;
  let elevated = false;
  for (let round = 0; round < 4 && current.program && TRANSPARENT.has(current.program); round += 1) {
    if (ELEVATING.has(current.program)) elevated = true;
    const at = current.args.findIndex((a) => !PREFIX_NOISE.test(a.text));
    // `env` and `time` with nothing after them run nothing; they are the
    // command, and peeling to an empty program would erase them.
    if (at < 0) break;
    const real = current.args[at];
    current = {
      ...current,
      program: real.resolved ? basename(real.value) : null,
      programRaw: real.text,
      args: current.args.slice(at + 1),
      peeled: [...(current.peeled || []), current.program],
    };
  }
  return elevated ? { ...current, elevated: true } : current;
}

// -- interpreters ---------------------------------------------------------

// Programs that run their argument instead of reading it. The payload's
// language is named here because it decides which grammar parses it, and
// guessing from the payload's own text is how a shell one-liner gets read as
// Python.
const INTERPRETERS = [
  { programs: ["sh", "bash", "zsh", "dash", "ksh", "ash", "busybox"], flags: ["-c"], language: "bash" },
  { programs: ["powershell", "pwsh"], flags: ["-command", "-c"], language: "powershell" },
  { programs: ["python", "python2", "python3", "py"], flags: ["-c"], language: "python" },
  { programs: ["node", "nodejs", "deno", "bun"], flags: ["-e", "--eval", "-p", "--print"], language: "javascript" },
  { programs: ["perl", "ruby"], flags: ["-e"], language: null },
];
// `-EncodedCommand` carries base64 UTF-16LE. Decoding it is not decoding an
// obfuscation for its own sake: without it the command reads as a literal
// token and every capability inside it is invisible.
const ENCODED_FLAGS = new Set(["-encodedcommand", "-enc", "-ec", "-e"]);

function decodeEncodedCommand(value) {
  try {
    const binary = atob(String(value).trim());
    let out = "";
    // UTF-16LE, which is what PowerShell encodes; a stray odd byte just ends it.
    for (let i = 0; i + 1 < binary.length; i += 2) {
      out += String.fromCharCode(binary.charCodeAt(i) | (binary.charCodeAt(i + 1) << 8));
    }
    return out.replace(/\0+$/, "") || null;
  } catch {
    return null;
  }
}

/** The payload a command runs in another language, or null. */
export function interpreterPayload(command) {
  if (!command?.program) return null;
  const spec = INTERPRETERS.find((i) => i.programs.includes(command.program));
  if (!spec) return null;

  if (command.heredoc && (spec.language || command.program.startsWith("py"))) {
    return { language: spec.language, source: command.heredoc, via: `heredoc:${command.program}` };
  }
  for (let i = 0; i < command.args.length; i += 1) {
    const flag = command.args[i].text.toLowerCase();
    const next = command.args[i + 1];
    if (!next) continue;
    if ((command.program === "powershell" || command.program === "pwsh") && ENCODED_FLAGS.has(flag)) {
      const decoded = next.resolved ? decodeEncodedCommand(next.value) : null;
      if (decoded) return { language: "powershell", source: decoded, via: `encoded:${command.program}` };
      continue;
    }
    if (!spec.flags.includes(flag)) continue;
    // An unresolved payload is the honest end of the walk: the bytes that would
    // have been run are not in the trace.
    if (!next.resolved || !spec.language) return null;
    return { language: spec.language, source: next.value, via: `flag:${command.program}${flag}` };
  }
  return null;
}

// -- the walk -------------------------------------------------------------

const COMMAND_TYPE = { bash: "command", powershell: "command" };

function collectCommands(root, language) {
  const type = COMMAND_TYPE[language];
  const found = [];
  const stack = [root];
  while (stack.length && found.length < MAX_COMMANDS) {
    const node = stack.pop();
    if (node.type === type) {
      const read = language === "bash" ? readBashCommand(node) : readPowershellCommand(node);
      if (read) found.push(read);
    }
    for (let i = node.namedChildCount - 1; i >= 0; i -= 1) stack.push(node.namedChild(i));
  }
  found.sort((a, b) => a.start - b.start);
  return found;
}

// A literal PowerShell string's printed value, or null. Double quotes expand
// `$` and backtick escapes, so only a double-quoted string with neither is
// known before it runs; single quotes are verbatim, with `''` for a quote.
export function powershellLiteral(text) {
  const t = String(text ?? "");
  if (t.length >= 2 && t[0] === "'" && t.endsWith("'")) return t.slice(1, -1).replace(/''/g, "'");
  if (t.length >= 2 && t[0] === '"' && t.endsWith('"') && !/[$`]/.test(t.slice(1, -1))) return t.slice(1, -1);
  return null;
}

// The string a single-expression statement reduces to: each expression level
// wraps the next in one child, down to the literal.
function bareString(node) {
  let current = node;
  while (current && current.type !== "string_literal" && current.namedChildCount === 1) current = current.namedChild(0);
  return current?.type === "string_literal" ? powershellLiteral(current.text) : undefined;
}

/**
 * The top-level statements of a PowerShell source, in order, as values: what
 * each one is and where it sits. Commands alone cannot say this - a bare
 * string prints itself and an assignment prints nothing, and neither is a
 * command. `kind` is "commands" (one pipeline of commands), "string" (a bare
 * string; `value` is what it prints, null when it is not literal),
 * "assignment", "empty", or "other" for anything else a reader cannot follow.
 */
function powershellStatements(root) {
  const childrenOf = (node) => {
    const out = [];
    for (let i = 0; i < (node?.namedChildCount || 0); i += 1) out.push(node.namedChild(i));
    return out;
  };
  const list = childrenOf(root).find((n) => n.type === "statement_list");
  const statements = [];
  for (const node of childrenOf(list).slice(0, MAX_COMMANDS)) {
    const at = { start: node.startIndex, end: node.endIndex };
    const first = node.type === "pipeline" ? node.namedChild(0) : null;
    const parts = first?.type === "pipeline_chain" ? childrenOf(first) : [];
    let kind = "other";
    let value;
    if (node.type === "empty_statement") kind = "empty";
    else if (first?.type === "assignment_expression") kind = "assignment";
    else if (parts.length && parts.every((p) => p.type === "command")) kind = "commands";
    else if (parts.length === 1 && (value = bareString(parts[0])) !== undefined) kind = "string";
    statements.push(kind === "string" ? { kind, value, ...at } : { kind, ...at });
  }
  return statements;
}

/**
 * Every command in one shell source, including the ones inside substitutions
 * and inside interpreter payloads, flattened in source order.
 *
 * @returns {{language, complete, truncated, commands, nested, depthReached}|null}
 *   `complete` is false when the parse hit an error, the source was clipped
 *   upstream, or a nested payload could not be followed. A caller that treats
 *   an incomplete reading as an exhaustive one is making exactly the claim this
 *   module exists to keep it from making.
 */
export async function readShellCommands(source, { language = "bash", clipped = false, depth = 0 } = {}) {
  if (!SHELL_LANGUAGES.includes(language)) return null;
  const parsed = await parseSource(language, source, { clipped });
  if (!parsed) return null;

  const commands = collectCommands(parsed.root, language).map((raw) => {
    const c = peelTransparent(raw);
    return {
      ...c,
      depth,
      via: null,
      resolved: Boolean(c.program) && c.args.every((a) => a.resolved) && c.redirects.every((r) => r.resolved),
    };
  });

  let complete = parseIsComplete(parsed) && commands.length < MAX_COMMANDS;
  const nested = [];
  if (depth >= MAX_NESTING_DEPTH) {
    // At the cap the reading is only incomplete if something was actually left
    // unfollowed. A chain that bottoms out in a real command at the last rung
    // hid nothing, and reporting it incomplete would push work at the judge
    // that a caller could have cleared.
    if (commands.some((c) => interpreterPayload(c))) complete = false;
  } else {
    for (const command of commands) {
      const payload = interpreterPayload(command);
      if (!payload) {
        // An interpreter whose payload could not be recovered is a hole in the
        // reading, not an absence of work.
        if (command.program && INTERPRETERS.some((i) => i.programs.includes(command.program))) complete = false;
        continue;
      }
      if (SHELL_LANGUAGES.includes(payload.language)) {
        const inner = await readShellCommands(payload.source, { language: payload.language, depth: depth + 1 });
        if (!inner) { complete = false; continue; }
        complete = complete && inner.complete;
        for (const c of inner.commands) nested.push({ ...c, via: payload.via, parent: command.start });
        nested.push(...inner.nested);
      } else {
        // Python and JavaScript payloads are real programs, but they are not
        // commands: they get parsed by their own grammar elsewhere. Recorded
        // here so the reading knows work exists that it did not flatten.
        nested.push({ language: payload.language, source: payload.source, via: payload.via, parent: command.start, opaque: true });
        complete = false;
      }
    }
  }

  return {
    language,
    complete,
    truncated: parsed.truncated,
    clipped: parsed.clipped,
    hasError: parsed.hasError,
    commands,
    nested,
    depthReached: depth,
    // PowerShell's top-level statements, which a reader needs to split one
    // call's output by statement; bash spells its statements in the source
    // between the commands, so it needs none.
    statements: language === "powershell" ? powershellStatements(parsed.root) : null,
  };
}
